'use client'

import { ComponentProps, FunctionComponent } from 'react'

import {
  Accessories,
  LabCoat,
  Outerwear,
  ScrubCap,
  ScrubPants,
  ScrubTop,
  Socks,
  type SortingState,
  Underscrubs,
} from '@syconium/little-miss-figgy'
import { BaseIcon } from '@syconium/little-miss-figgy/dist/components/Icons/BaseIcon'

import {
  OrganizationEmbroideryTextFont,
  OrganizationEmbroideryTextThreadColor,
  OrganizationLogo,
  OrganizationLogoEmbroideryPlacementSelection,
  OrganizationTextEmbroideryPlacementSelection,
  Organization_Collection_Statuses,
  ProductCatalogCategoryKey,
  ProductCatalogEmbroideryPlacement,
} from '../../../../__generated__/graphql/catalog/graphql'
import {
  GenericTextEmbroideryColorKey,
  GenericTextEmbroideryThreadColorEnum,
} from '../../../../containers/EmbroideryContainer/utils/embroideryThreadOptions'

/**
 * Builds the `sort` GraphQL input argument from a table's `SortingState`, restricted to
 * whatever attributes a given organization query is allowed to sort by.
 */
export function buildOrganizationSortInput<TAttr extends string>(
  sorting: SortingState,
  allowedAttributes: readonly TAttr[]
): { attr: TAttr; dir: 'ASC' | 'DESC' } | null {
  const targetSort = sorting[0]
  if (targetSort && (allowedAttributes as readonly string[]).includes(targetSort.id)) {
    return {
      attr: targetSort.id as TAttr,
      dir: targetSort.desc ? 'DESC' : 'ASC',
    }
  }
  return null
}

/** Type guard for checking if a given value is a valid resource status value. */
export function isValidResourceStatusValue(
  value: string | undefined | null
): value is Organization_Collection_Statuses {
  return value === 'ACTIVE' || value === 'ARCHIVED'
}

/**
 * A minimal set of constants that can be derived from a given `OrganizationLogo` based
 * on it's archived/approval status.
 *
 * This is used purely for presentational purposes in the UI for displaying a user-friendly badge
 * indicating the status of a given logo. This enum is unrelated to the generated `LogoStatus` enum
 * that is defined by the organization service schema.
 */
export type LogoBadgeStatus = 'pending' | 'archived' | 'approved'
export function getLogoBadgeStatus(
  organizationLogo: Pick<OrganizationLogo, 'archivedAt' | 'approved'>
): LogoBadgeStatus {
  if (organizationLogo.archivedAt) return 'archived'
  if (organizationLogo.approved) return 'approved'
  return 'pending'
}

const textPlacementSelectionToOffsetKeyMap = {
  LEFT_CHEST: {
    position: 'LEFT',
    location: 'CHEST',
  },
  RIGHT_CHEST: {
    position: 'RIGHT',
    location: 'CHEST',
  },
  LEFT_SIDE: {
    position: 'LEFT',
    location: 'SIDE',
  },
  RIGHT_SIDE: {
    position: 'RIGHT',
    location: 'SIDE',
  },
  LEFT_SLEEVE: {
    position: 'LEFT',
    location: 'SLEEVE',
  },
  RIGHT_SLEEVE: {
    position: 'RIGHT',
    location: 'SLEEVE',
  },
  FRONT: null,
  BACK: null,
} as const satisfies Record<
  OrganizationTextEmbroideryPlacementSelection | OrganizationLogoEmbroideryPlacementSelection,
  ProductCatalogEmbroideryPlacement | null
>

/**
 * Convert a given `OrganizationTextEmbroideryPlacementSelection` or `OrganizationLogoEmbroideryPlacementSelection` to
 * the cooresponding `ProductCatalogEmbroideryPlacement` object equivalent.
 */
export function getEmbroideryPlacementFromOrganizationPlacementSelection(
  placementSelection:
    | (OrganizationTextEmbroideryPlacementSelection | OrganizationLogoEmbroideryPlacementSelection)
    | null
    | undefined
) {
  return placementSelection ? textPlacementSelectionToOffsetKeyMap[placementSelection] : null
}

const threadColorToColorKeyMap = {
  BLACK: GenericTextEmbroideryThreadColorEnum.Black,
  WHITE: GenericTextEmbroideryThreadColorEnum.White,
} as const satisfies Record<OrganizationEmbroideryTextThreadColor, GenericTextEmbroideryColorKey>

/** Get the `TextEmbroideryColorKey` that is associated with a given `EmbroideryTextThreadColor` value. Useful for converting a typed thread color to one of our manual constants for black/white text. */
export function getTextEmbroideryColorKeyFromEmbroideryTextThreadColor(
  threadColor: OrganizationEmbroideryTextThreadColor | null | undefined
) {
  return threadColor ? threadColorToColorKeyMap[threadColor] : null
}

/** Get the string that is expected to be communicated by `ProductCatalogEmbroideryConfiguration.fontRestrictions` from a given `OrganizationEmbroideryTextFont`. */
export function getFontFaceRestrictionKeyFromOrganizationEmbroideryTextFont(
  font: OrganizationEmbroideryTextFont
): 'Script' | 'Block' {
  if (font === 'BLOCK') return 'Block'
  return 'Script'
}

const categoryKeyMap: Record<string, ProductCatalogCategoryKey> = {
  'Scrub Tops': 'SCRUB_TOP',
  'Scrub Top': 'SCRUB_TOP',
  'Jumpsuits': 'JUMPSUITS',
  'Scrub Pants': 'SCRUB_PANTS',
  'Lab Coats': 'LAB_COATS',
  'Outerwear': 'OUTERWEAR',
  'Scrub Jacket': 'OUTERWEAR',
  'Scrub Cap': 'SCRUB_CAP',
  'Socks': 'SOCKS',
  'Underscrubs': 'UNDERSCRUB',
  'Underscrub': 'UNDERSCRUB',
  'Accessories': 'ACCESSORIES',
  'Shoes': 'SHOES',
}

/**
 * Get a `ProductCatalogCategoryKey` that is related to a given raw category value.
 *
 * Note: This should not be needed once the entire application is relying on the schema exposed by the catalog service, which exposes these
 * these keys for a product category. This is only needed in order to feign compatibility between Zidi/Collection Service with logic that
 * assumes that we have the category key available.
 */
export function getCategoryKeyFromRawCategory(
  rawCategory: string | null | undefined
): ProductCatalogCategoryKey | undefined {
  if (!rawCategory) return 'OTHER'
  return categoryKeyMap[rawCategory]
}

export const categoryKeySortOrder: ProductCatalogCategoryKey[] = [
  'SCRUB_TOP',
  'SCRUB_PANTS',
  'JUMPSUITS',
  'UNDERSCRUB',
  'OUTERWEAR',
  'LAB_COATS',
  'SCRUB_CAP',
  'SOCKS',
  'LOUNGEWEAR',
  'ACCESSORIES',
]

/** A map of `ProductCatalogCategoryKey`'s to their associated SVG Icon component */
export const categoryLogoMap: Record<
  ProductCatalogCategoryKey,
  FunctionComponent<ComponentProps<typeof BaseIcon>>
> = {
  ACCESSORIES: Accessories,
  LAB_COATS: LabCoat,
  OUTERWEAR: Outerwear,
  SCRUB_CAP: ScrubCap,
  SCRUB_PANTS: ScrubPants,
  SCRUB_TOP: ScrubTop,
  SOCKS: Socks,
  UNDERSCRUB: Underscrubs,

  // These categories do not have a dedicated icon, so we fall back to using the `Accessories` icon (or an acceptable existing alternative)
  JUMPSUITS: ScrubTop,
  LOUNGEWEAR: ScrubTop,
  EMBROIDERY: Accessories,
  GIFT: Accessories,
  GIFT_CARD: Accessories,
  OTHER: Accessories,
  SHOES: Accessories,
}

/** The canonical TEAMS core collection handle. */
export const TEAMS_CORE_COLLECTION_HANDLE = 'teams-core-products'

/** Search param set on the wizard's redirect URL after a plain collection creation. */
export const COLLECTION_CREATED_PARAM = 'collection_created'

/** Search param set on the wizard's redirect URL after auto-assigning a newly created collection. */
export const COLLECTION_ASSIGNED_PARAM = 'collection_assigned'

/** Create the API path for uploading a CSV of organization domains for a given organization ID. */
export const createOrganizationDomainsCsvUploadPath = (organizationId: string) =>
  `/api/organizations/v1/organizations/${organizationId}/domains/csv`

/** Create the API path for uploading a CSV of team members scoped to a single Team Store. */
export const createTeamStoreTeamMembersCsvUploadPath = (teamStoreId: string) =>
  `/api/organizations/v1/team-stores/${teamStoreId}/members/csv`

/** Create the API path for uploading a CSV of email domains scoped to a single Team Store. */
export const createTeamStoreDomainsCsvUploadPath = (teamStoreId: string) =>
  `/api/organizations/v1/team-stores/${teamStoreId}/domains/csv`

/** Create the API path for uploading a CSV of organization team members for a given organization ID. */
export const createOrganizationTeamMembersCsvUploadPath = (organizationId: string) =>
  `/api/organizations/v1/organizations/${organizationId}/members/csv`

export const organizationDomainsCsvTemplateContent = 'domain'
export const organizationTeamMembersCsvTemplateContent = 'first_name,last_name,email'

/**
 * For a given GraphQL error message, extract the relevant domain names if the error is identified as mentioning an invalid email address.
 *
 * This is used for some requests from the organization service where the backend maintains a list of disallowed
 * email domains. This allows us to present localized error messages while providing the user context of what email
 * domains were considered invalid.
 */
export const parseInvalidDomainGraphQLError = (errorMessage: string) => {
  const isInvalidDomainError = errorMessage.includes('Invalid email domain')

  if (isInvalidDomainError) {
    // Extract domain(s) from either "Invalid email domain foo.com" or "Invalid email domains: [foo.com, bar.com]"
    const match =
      errorMessage.match(/Invalid email domain\s+(.+)/) ||
      errorMessage.match(/Invalid email domains:\s+\[(.+)\]/)

    const domains = match ? match[1] : ''

    return domains
  }

  return undefined
}

/**
 * Resolves a localized form-level error message for any organization service mutation that can fail
 * with an "Invalid email domain" error. Falls back to the provided generic message for unrelated errors.
 */
export const formatInvalidDomainErrorMessage = ({
  errorMessage,
  invalidEmailDomainTemplate,
  fallbackMessage,
}: {
  errorMessage: string
  invalidEmailDomainTemplate: string
  fallbackMessage: string
}): string => {
  const invalidDomains = parseInvalidDomainGraphQLError(errorMessage)
  if (invalidDomains) {
    return invalidEmailDomainTemplate.replace('{{domain}}', invalidDomains)
  }
  return fallbackMessage
}
